import { lazy, Suspense } from 'react';

import type { RichTextEditorProps } from './RichTextEditor.js';

/**
 * The editor, code-split.
 *
 * TipTap and ProseMirror are ~135 kB gzipped. Imported directly they land in the dashboard's single
 * entry chunk, which every screen downloads — including `MyClock`, the one a crew member opens on a
 * phone in a field with one bar. That screen already has an AbortSignal.timeout on its punch
 * because the network there is genuinely bad; making it fetch an editor it never renders would be
 * working against that.
 *
 * Only the estimate and invoice builders import this, so only those routes pay for it. Everything
 * else is unchanged.
 *
 * Import this, not `RichTextEditor.tsx` — a direct import anywhere pulls the whole editor back into
 * the main chunk and silently undoes the split. The tests import the real component on purpose;
 * they are not bundled.
 */
const Editor = lazy(() =>
  import('./RichTextEditor.js').then((module) => ({ default: module.RichTextEditor })),
);

export type { RichTextEditorProps };

export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <Suspense
      fallback={
        // Sized to the editor's own min-height so the form does not jump when the chunk lands.
        // aria-busy rather than a spinner: this is a field in a form, and a status role here would
        // announce itself over whatever the user is actually doing.
        <div
          aria-busy="true"
          className="min-h-32 w-full rounded-md border border-border bg-background"
        />
      }
    >
      <Editor {...props} />
    </Suspense>
  );
}
